(function () {
  // ===== Config =====
  const TRIGGER_TAGS = new Set(['accessories']);      // lower-case tags that should trigger
  const HANDLE_KEYWORDS = ['stem-cap','valve','nipple','spoke','axle','end-cap'];
  const TARGET_SELECTOR = '#CartPopup > div > div.upcart-cart-body.styles_CartPreview__body__';

  // ===== Styles (injected once) =====
  function injectStyles() {
    if (document.getElementById('tariff-note-styles')) return;
    const css = `
      /* ---- Tariff notice (scoped) --------------------------------------- */
      .tariff-note {
        display: flex;
        align-items: flex-start;
        gap: 10px;

        /* Impulse-like density */
        padding: 10px 12px;
        margin: 8px 0;

        /* Subtle card */
        border-radius: 6px;
        border: 1px solid var(--upcart-border, rgba(0,0,0,.06));
        background: var(--upcart-bg-soft, #fafafa);
        color: var(--upcart-text, inherit);

        /* Type */
        font-size: 14px;
        line-height: 1.4;

        /* Soft appear (no layout shift) */
        opacity: 0;
        transform: translateY(4px);
        visibility: hidden;
        pointer-events: none;
        transition:
          opacity 160ms ease,
          transform 160ms ease,
          visibility 0ms linear 160ms;
      }
      /* Visible state controlled by data attribute */
      .tariff-note[data-show="true"] {
        opacity: 1;
        transform: translateY(0);
        visibility: visible;
        pointer-events: auto;
        transition:
          opacity 160ms ease,
          transform 160ms ease,
          visibility 0ms;
      }
      /* Respect reduced motion */
      @media (prefers-reduced-motion: reduce) {
        .tariff-note, .tariff-note[data-show="true"] {
          transition: none;
          transform: none;
        }
      }

      .tariff-note__icon { flex: 0 0 auto; width: 18px; height: 18px; margin-top: 1px; }
      .tariff-note__icon svg { display: block; width: 100%; height: 100%; }
      .tariff-note__text { flex: 1 1 auto; }
      .tariff-note strong { font-weight: 600; }

      /* Theme hooks (works for light/dark drawers) */
      #CartPopup, .upcart-drawer, [data-upcart-drawer] {
        --upcart-bg: var(--color-bg, #fff);
        --upcart-bg-soft: color-mix(in srgb, var(--upcart-bg) 92%, #000 8%);
        --upcart-text: var(--color-text, inherit);
        --upcart-border: var(--color-border, rgba(0,0,0,.06));
        --upcart-accent: var(--color-accent, #f6b73c); /* soft amber fallback */
      }
      /* Fallback for browsers without color-mix() */
      @supports not (background: color-mix(in srgb, #fff 90%, #000)) {
        #CartPopup, .upcart-drawer, [data-upcart-drawer] {
          --upcart-bg-soft: #f7f7f7;
        }
      }
      /* Dark drawers */
      .upcart-drawer [class*="dark"], #CartPopup [class*="dark"] {
        --upcart-bg: #111;
        --upcart-bg-soft: rgba(255,255,255,.06);
        --upcart-text: rgba(255,255,255,.92);
        --upcart-border: rgba(255,255,255,.14);
        --upcart-accent: #f6c05c;
      }
      /* Icon accent uses theme token */
      .tariff-note__icon path[fill="#accent"] { fill: var(--upcart-accent); }
    `.trim();
    const style = document.createElement('style');
    style.id = 'tariff-note-styles';
    style.textContent = css;
    document.head.appendChild(style);
  }

  // ===== Banner element =====
  function ensureBanner() {
    let el = document.getElementById('tariff-message');
    if (!el) {
      injectStyles();
      el = document.createElement('div');
      el.id = 'tariff-message';
      el.className = 'tariff-note';
      el.setAttribute('role', 'note');
      el.setAttribute('aria-live', 'polite'); // gentle announcement when shown
      el.innerHTML = `
        <div class="tariff-note__icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" focusable="false" aria-hidden="true">
            <path d="M1 21h22L12 2 1 21z" fill="#accent"></path>
            <rect x="11" y="9" width="2" height="6" rx="1" fill="#000" opacity=".75"></rect>
            <circle cx="12" cy="18" r="1.2" fill="#000" opacity=".75"></circle>
          </svg>
        </div>
        <div class="tariff-note__text">
          Small parts may incur <strong>$30+ customs/import fees</strong> at delivery (charged by your carrier).
        </div>
      `;
    }
    return el;
  }

  function mountBanner() {
    const target = document.querySelector(TARGET_SELECTOR);
    if (!target) return null;
    const banner = ensureBanner();
    if (!target.contains(banner)) target.appendChild(banner);
    return banner;
  }

  // ===== Cart/tag logic =====
  const tagCache = new Map();

  async function fetchCart() {
    const res = await fetch('/cart.js', { credentials: 'same-origin' });
    if (!res.ok) throw new Error('cart ' + res.status);
    return res.json();
  }

  function handleFromItem(item) {
    if (item?.handle && /^[a-z0-9-]+$/i.test(item.handle)) return item.handle.toLowerCase();
    const url = item?.url || item?.product_url || item?.product?.url;
    if (!url) return null;
    const i = url.indexOf('/products/');
    if (i === -1) return null;
    const tail = url.slice(i + '/products/'.length).split('?')[0].replace(/\/$/, '');
    return /^[a-z0-9-]+$/i.test(tail) ? tail.toLowerCase() : null;
  }

  async function tagsFor(handle) {
    if (!handle) return [];
    if (tagCache.has(handle)) return tagCache.get(handle);
    try {
      const res = await fetch(`/products/${handle}.js`, { credentials: 'same-origin' });
      if (!res.ok) { tagCache.set(handle, []); return []; }
      const data = await res.json();
      const tags = (data.tags || []).map(t => String(t).trim().toLowerCase());
      tagCache.set(handle, tags);
      return tags;
    } catch {
      tagCache.set(handle, []);
      return [];
    }
  }

  async function shouldShow() {
    const cart = await fetchCart();
    for (const it of (cart.items || [])) {
      const h = handleFromItem(it);
      if (h && HANDLE_KEYWORDS.some(k => h.includes(k))) return true;
      const tags = await tagsFor(h);
      if (tags.some(t => TRIGGER_TAGS.has(t))) return true;
    }
    return false;
  }

  // ===== Toggle banner (uses data attribute for smooth fade) =====
  async function updateBanner() {
    const banner = mountBanner();
    if (!banner) return;
    try {
      const show = await shouldShow();
      banner.setAttribute('data-show', show ? 'true' : 'false');
    } catch {
      banner.setAttribute('data-show', 'false');
    }
  }

  const debounce = (fn, ms=150) => { let t; return (...a)=>{ clearTimeout(t); t=setTimeout(()=>fn(...a), ms); }; };
  const updateDebounced = debounce(updateBanner, 120);

  // ===== Boot & observers =====
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', updateBanner);
  } else {
    updateBanner();
  }
  ['cart:updated','upcart:updated','upcart:item:added','upcart:item:removed']
    .forEach(ev => window.addEventListener(ev, updateDebounced, { passive:true }));
  new MutationObserver(updateDebounced).observe(document.body, { childList:true, subtree:true });
  setInterval(updateBanner, 3000);
})();